import type { WorkspaceElement } from '@/types/synoptique'

/**
 * Pure utility functions for calculating connection points between elements.
 * No React dependencies — can be used in any context.
 */

/** Set user-select with vendor prefixes (avoids TS errors) */
export function setUserSelect(element: HTMLElement, value: string) {
  const style = element.style as CSSStyleDeclaration & {
    webkitUserSelect?: string
    mozUserSelect?: string
    msUserSelect?: string
  }
  style.userSelect = value
  style.webkitUserSelect = value
  style.mozUserSelect = value
  style.msUserSelect = value
}

/** Calculate best 8-point connection on a bounding box */
export function getBestPointOnBounds(
  bounds: { x: number; y: number; width: number; height: number },
  targetX: number,
  targetY: number
): { x: number; y: number; side: string } {
  const { x, y, width, height } = bounds
  const points = [
    { x: x, y: y, side: 'top-left' },
    { x: x + width * 0.5, y: y, side: 'top' },
    { x: x + width, y: y, side: 'top-right' },
    { x: x + width, y: y + height * 0.5, side: 'right' },
    { x: x + width, y: y + height, side: 'bottom-right' },
    { x: x + width * 0.5, y: y + height, side: 'bottom' },
    { x: x, y: y + height, side: 'bottom-left' },
    { x: x, y: y + height * 0.5, side: 'left' },
  ]

  let minDistance = Infinity
  let bestPoint = points[0]

  points.forEach(point => {
    const distance = Math.sqrt(
      Math.pow(point.x - targetX, 2) + Math.pow(point.y - targetY, 2)
    )
    if (distance < minDistance) {
      minDistance = distance
      bestPoint = point
    }
  })

  return bestPoint
}

/** Calculate intersection point on panel edge from a source point */
export function getPanelEdgePoint(
  procX: number,
  procY: number,
  panel: WorkspaceElement
): { x: number; y: number } {
  const panelLeft = panel.position.x
  const panelTop = panel.position.y
  const panelRight = panel.position.x + panel.size.width
  const panelBottom = panel.position.y + panel.size.height
  const panelCenterX = panel.position.x + panel.size.width / 2
  const panelCenterY = panel.position.y + panel.size.height / 2

  const dx = panelCenterX - procX
  const dy = panelCenterY - procY

  const intersections: Array<{ x: number; y: number; distance: number }> = []

  // Left side (x = panelLeft)
  if (dx !== 0) {
    const t = (panelLeft - procX) / dx
    if (t > 0 && t <= 1) {
      const y = procY + t * dy
      if (y >= panelTop && y <= panelBottom) {
        intersections.push({ x: panelLeft, y, distance: t })
      }
    }
  }

  // Right side (x = panelRight)
  if (dx !== 0) {
    const t = (panelRight - procX) / dx
    if (t > 0 && t <= 1) {
      const y = procY + t * dy
      if (y >= panelTop && y <= panelBottom) {
        intersections.push({ x: panelRight, y, distance: t })
      }
    }
  }

  // Top side (y = panelTop)
  if (dy !== 0) {
    const t = (panelTop - procY) / dy
    if (t > 0 && t <= 1) {
      const x = procX + t * dx
      if (x >= panelLeft && x <= panelRight) {
        intersections.push({ x, y: panelTop, distance: t })
      }
    }
  }

  // Bottom side (y = panelBottom)
  if (dy !== 0) {
    const t = (panelBottom - procY) / dy
    if (t > 0 && t <= 1) {
      const x = procX + t * dx
      if (x >= panelLeft && x <= panelRight) {
        intersections.push({ x, y: panelBottom, distance: t })
      }
    }
  }

  if (intersections.length > 0) {
    intersections.sort((a, b) => a.distance - b.distance)
    return { x: intersections[0].x, y: intersections[0].y }
  }

  return { x: panelCenterX, y: panelCenterY }
}

/** Get mid-side point of panel closest to (fromX, fromY) */
export function getPanelMidSidePoint(
  fromX: number,
  fromY: number,
  panel: WorkspaceElement
): { x: number; y: number } {
  const cx = panel.position.x + panel.size.width / 2
  const cy = panel.position.y + panel.size.height / 2
  const dx = cx - fromX
  const dy = cy - fromY
  if (Math.abs(dx) >= Math.abs(dy)) {
    return {
      x: dx > 0 ? panel.position.x : panel.position.x + panel.size.width,
      y: cy
    }
  } else {
    return {
      x: cx,
      y: dy > 0 ? panel.position.y : panel.position.y + panel.size.height
    }
  }
}

/** Get best connection point on a cabinet (8-point) */
export function getBestCabinetPoint(
  cabinet: WorkspaceElement,
  x: number,
  y: number
): { x: number; y: number } {
  const cx = cabinet.position.x
  const cy = cabinet.position.y
  const cw = cabinet.size.width
  const ch = cabinet.size.height
  const pts = [
    { x: cx, y: cy },
    { x: cx + cw * 0.5, y: cy },
    { x: cx + cw, y: cy },
    { x: cx + cw, y: cy + ch * 0.5 },
    { x: cx + cw, y: cy + ch },
    { x: cx + cw * 0.5, y: cy + ch },
    { x: cx, y: cy + ch },
    { x: cx, y: cy + ch * 0.5 },
  ]
  let best = pts[0]
  let dmin = Infinity
  pts.forEach(p => {
    const d = Math.hypot(p.x - x, p.y - y)
    if (d < dmin) { dmin = d; best = p }
  })
  return best
}

/** Calculate actual image bounds (accounting for preserveAspectRatio) */
export function calculateActualImageBounds(
  element: WorkspaceElement,
  imageDims: { width: number; height: number } | undefined
): { x: number; y: number; width: number; height: number } {
  if (!imageDims) {
    return {
      x: element.position.x,
      y: element.position.y,
      width: element.size.width,
      height: element.size.height
    }
  }

  const imageRatio = imageDims.width / imageDims.height
  const containerRatio = element.size.width / element.size.height

  let actualWidth: number, actualHeight: number, offsetX: number, offsetY: number

  if (imageRatio > containerRatio) {
    actualWidth = element.size.width
    actualHeight = element.size.width / imageRatio
    offsetX = 0
    offsetY = (element.size.height - actualHeight) / 2
  } else {
    actualHeight = element.size.height
    actualWidth = element.size.height * imageRatio
    offsetX = (element.size.width - actualWidth) / 2
    offsetY = 0
  }

  return {
    x: element.position.x + offsetX,
    y: element.position.y + offsetY,
    width: actualWidth,
    height: actualHeight
  }
}

/** Convert image URL to base64 data URI */
export async function imageUrlToBase64(url: string): Promise<string> {
  try {
    const response = await fetch(url)
    if (!response.ok) return url
    const blob = await response.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(url)
      reader.readAsDataURL(blob)
    })
  } catch {
    return url
  }
}
